import React from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import HomePage from "./pages/Home";
import LoginPage from "./pages/auth/Login";
import RegisterPage from "./pages/auth/Register";
import AutoLoginPage from "./pages/AutoLogin";
import MarketplacePage from "./pages/Marketplace";
import ListingDetailPage from "./pages/ListingDetail";
import CheckoutPage from "./pages/Checkout";
import AalignCampaignsPage from "./pages/AalignCampaigns";
import AalignCampaignDetailPage from "./pages/AalignCampaignDetail";
import SellersMarketplacePage from "./pages/SellersMarketplace";
import MyBookingsPage from "./pages/MyBookings";
import MyListingsPage from "./pages/MyListings";

// Buyer Pages
import BuyerDashboard from "./pages/buyer/Dashboard";
import BuyerOrderDetail from "./pages/buyer/OrderDetail";

// Seller Pages
import SellerDashboard from "./pages/seller/Dashboard";
import SellerOrderDetail from "./pages/seller/OrderDetail";
import CreateListingPage from "./pages/seller/CreateListing";

// Admin Pages
import AdminDashboard from "./pages/admin/Dashboard";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // Redirect to appropriate dashboard based on role
    if (user?.role === 'admin') return <Navigate to="/admin" replace />;
    if (user?.role === 'seller') return <Navigate to="/seller/dashboard" replace />;
    return <Navigate to="/buyer/dashboard" replace />;
  }

  return children;
};

// Public Route (redirect if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    // Redirect to appropriate dashboard
    if (user?.role === 'admin') return <Navigate to="/admin" replace />;
    if (user?.role === 'seller') return <Navigate to="/seller/dashboard" replace />;
    return <Navigate to="/buyer/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/marketplace" element={<MarketplacePage />} />
      <Route path="/listing/:id" element={<ListingDetailPage />} />
      
      {/* Auto-Login from AALIGN Platform */}
      <Route path="/auto-login" element={<AutoLoginPage />} />
      
      {/* Seller Marketplace for Buyers */}
      <Route
        path="/marketplace/sellers"
        element={
          <ProtectedRoute allowedRoles={['buyer']}>
            <SellersMarketplacePage />
          </ProtectedRoute>
        }
      />
      
      {/* AALIGN Campaigns - Seller/Admin only */}
      <Route
        path="/aalign/campaigns"
        element={
          <ProtectedRoute allowedRoles={['seller', 'admin']}>
            <AalignCampaignsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/aalign/campaign/:id"
        element={
          <ProtectedRoute allowedRoles={['seller', 'admin']}>
            <AalignCampaignDetailPage />
          </ProtectedRoute>
        }
      />
      
      {/* My Bookings - Seller only */}
      <Route
        path="/my-bookings"
        element={
          <ProtectedRoute allowedRoles={['seller']}>
            <MyBookingsPage />
          </ProtectedRoute>
        }
      />
      
      {/* My Listings - Buyer only */}
      <Route
        path="/my-listings"
        element={
          <ProtectedRoute allowedRoles={['buyer']}>
            <MyListingsPage />
          </ProtectedRoute>
        }
      />
      
      {/* Campaign Detail - Accessible by both buyers and sellers */}
      <Route
        path="/campaign/:id"
        element={
          <ProtectedRoute allowedRoles={['buyer', 'seller']}>
            <AalignCampaignDetailPage />
          </ProtectedRoute>
        }
      />
      
      {/* Auth Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />

      {/* Buyer Routes */}
      <Route
        path="/checkout/:id"
        element={
          <ProtectedRoute allowedRoles={['buyer']}>
            <CheckoutPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/buyer/dashboard"
        element={
          <ProtectedRoute allowedRoles={['buyer']}>
            <BuyerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/buyer/orders"
        element={
          <ProtectedRoute allowedRoles={['buyer']}>
            <BuyerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/buyer/orders/:id"
        element={
          <ProtectedRoute allowedRoles={['buyer']}>
            <BuyerOrderDetail />
          </ProtectedRoute>
        }
      />

      {/* Seller Routes */}
      <Route
        path="/seller/dashboard"
        element={
          <ProtectedRoute allowedRoles={['seller']}>
            <SellerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller/orders"
        element={
          <ProtectedRoute allowedRoles={['seller']}>
            <SellerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller/orders/:id"
        element={
          <ProtectedRoute allowedRoles={['seller']}>
            <SellerOrderDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller/listings"
        element={
          <ProtectedRoute allowedRoles={['seller']}>
            <SellerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller/listings/new"
        element={
          <ProtectedRoute allowedRoles={['seller']}>
            <CreateListingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller/onboarding"
        element={
          <ProtectedRoute allowedRoles={['seller']}>
            <SellerDashboard />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
